import { Alert, Descriptions, Drawer, Empty, Image, Space, Spin, Tag, Typography, theme } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { useAppFeedbackDetail } from '@/hooks/useSystemAdminApi';
import { AppFeedbackDeliveryStatus } from './AppFeedbackDeliveryStatus';
import { SystemReleaseTag } from '@/pages/feedback-review/system_release_tag';
import { systemReleaseLabel } from '@/pages/feedback-review/feedback_review_utils';
import {
    feedbackCategoryMeta,
    formatDimensions,
    formatFeedbackCreatedAt,
    formatLogTime,
    isSafeHttpUrl,
    logLevelTone,
    type LogTone,
} from './app_feedback_utils';

const { Paragraph, Text, Title } = Typography;

interface AppFeedbackDetailDrawerProps {
    /** Entry to show; `null` closes the drawer. */
    feedbackId: string | null;
    onClose: () => void;
}

/**
 * Detail view of one in-app feedback entry: full message, screenshot and
 * further images (zoomable), browser/display data and the captured browser
 * console (monospace, colored by level). Loads the entry on its own
 * (`GET /system-admin/app-feedback/:id`) — the list carries no logs/URLs.
 */
export default function AppFeedbackDetailDrawer({ feedbackId, onClose }: AppFeedbackDetailDrawerProps) {
    const { token } = theme.useToken();
    const { data: item, isLoading, isError } = useAppFeedbackDetail(feedbackId);

    const toneColor: Record<LogTone, string> = {
        error: token.colorError,
        warning: token.colorWarning,
        info: token.colorInfo,
        default: token.colorText,
    };

    const category = item ? feedbackCategoryMeta(item.category) : null;
    const systemRelease = systemReleaseLabel(item?.systemReleaseReason);
    const imageAttachments = item?.attachments.filter((a) => a.contentType.startsWith('image/')) ?? [];
    const otherAttachments = item?.attachments.filter((a) => !a.contentType.startsWith('image/')) ?? [];
    const pixelRatio = item?.devicePixelRatio;

    return (
        <Drawer
            title={
                item && category ? (
                    <Space>
                        <Tag color={category.color}>{category.label}</Tag>
                        <span>Feedback vom {formatFeedbackCreatedAt(item.createdAt)}</span>
                    </Space>
                ) : (
                    'Feedback'
                )
            }
            open={!!feedbackId}
            onClose={onClose}
            size={760}
            destroyOnHidden
        >
            {isLoading && (
                <div style={{ textAlign: 'center', padding: 48 }}>
                    <Spin />
                </div>
            )}
            {isError && <Alert type="error" showIcon title="Feedback konnte nicht geladen werden." />}
            {item && (
                <Space orientation="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Title level={5}>Nachricht</Title>
                        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                            {item.message}
                        </Paragraph>
                    </div>

                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="Mandant">{item.tenantName ?? item.tenantId}</Descriptions.Item>
                        <Descriptions.Item label="Gemeldet von">
                            {item.createdByEmail ?? item.createdByUserId ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Seite">
                            {item.pageUrl ? (
                                isSafeHttpUrl(item.pageUrl) ? (
                                    <a href={item.pageUrl} target="_blank" rel="noreferrer">
                                        {item.pageUrl}
                                    </a>
                                ) : (
                                    item.pageUrl
                                )
                            ) : (
                                '—'
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mail an Support">
                            <AppFeedbackDeliveryStatus
                                reviewStatus={item.reviewStatus}
                                emailSentAt={item.emailSentAt}
                                systemReleaseReason={null}
                            />
                        </Descriptions.Item>
                        {systemRelease && (
                            <Descriptions.Item label="Weitergabe">
                                <SystemReleaseTag reason={item.systemReleaseReason} /> {systemRelease.sentence}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Browser">{item.userAgent ?? '—'}</Descriptions.Item>
                        <Descriptions.Item label="Fenster (Viewport)">
                            {formatDimensions(item.viewportWidth, item.viewportHeight)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Bildschirm">
                            {formatDimensions(item.screenWidth, item.screenHeight)}
                            {pixelRatio ? ` · Pixel-Ratio ${pixelRatio}` : ''}
                        </Descriptions.Item>
                        <Descriptions.Item label="Feedback-ID">
                            <Text copyable>{item.id}</Text>
                        </Descriptions.Item>
                    </Descriptions>

                    <div>
                        <Title level={5}>Screenshot</Title>
                        {item.screenshotUrl ? (
                            <Image
                                src={item.screenshotUrl}
                                alt="Screenshot der Seite"
                                style={{ maxWidth: '100%', border: `1px solid ${token.colorBorder}` }}
                            />
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Kein Screenshot erfasst" />
                        )}
                    </div>

                    {(imageAttachments.length > 0 || otherAttachments.length > 0) && (
                        <div>
                            <Title level={5}>Weitere Bilder / Anhänge ({item.attachments.length})</Title>
                            {imageAttachments.length > 0 && (
                                <Image.PreviewGroup>
                                    <Space wrap>
                                        {imageAttachments.map((a) => (
                                            <Image
                                                key={a.url}
                                                src={a.url}
                                                alt={a.filename}
                                                width={160}
                                                height={120}
                                                style={{
                                                    objectFit: 'cover',
                                                    border: `1px solid ${token.colorBorder}`,
                                                }}
                                            />
                                        ))}
                                    </Space>
                                </Image.PreviewGroup>
                            )}
                            {otherAttachments.map((a) => (
                                <div key={a.url}>
                                    <a href={a.url} target="_blank" rel="noreferrer">
                                        <PaperClipOutlined /> {a.filename}
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}

                    <div>
                        <Title level={5}>Browser-Konsole ({item.browserLogs.length} Einträge)</Title>
                        {item.browserLogs.length === 0 ? (
                            <Text type="secondary">Keine Konsolenausgaben erfasst.</Text>
                        ) : (
                            <pre
                                style={{
                                    fontFamily: token.fontFamilyCode,
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                    background: token.colorFillTertiary,
                                    borderRadius: token.borderRadius,
                                    padding: '8px 12px',
                                    maxHeight: 420,
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    margin: 0,
                                }}
                            >
                                {item.browserLogs.map((entry, idx) => (
                                    <div key={idx} style={{ color: toneColor[logLevelTone(entry.level)] }}>
                                        <span style={{ color: token.colorTextTertiary }}>
                                            {formatLogTime(entry.timestamp)}
                                        </span>{' '}
                                        [{entry.level.toUpperCase()}] {entry.message}
                                    </div>
                                ))}
                            </pre>
                        )}
                    </div>
                </Space>
            )}
        </Drawer>
    );
}
